import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth0 } from '@auth0/auth0-react';
import {
    Button,
    Form,
    Table,
    Row,
    Col,
    ButtonGroup,
    ListGroup,
    Dropdown,
    Container,
    Tab,
    Tabs,
    Badge,
    Accordion
} from 'react-bootstrap';
import '../styles/main.css';
import { config } from '../config.js';
import { GetConnectionStatus } from "../utils/GetConnectionStatus";
import GroupNameModal from "./GroupNameModal.jsx";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { routeToServerlessAPI } from "../utils/errorHandler.js";
import { isAuthorized } from "../utils/Permissions.js";
import { getMountainTime } from "../utils/time.js";
import { TradingZoneModal } from "./TradingZones/TradingZonesModal.jsx";
import { InfoHover } from "./InfoHover/InfoHover.jsx";
import { TradingZonesTable } from "./TradingZones/TradingZonesTable.jsx";
import { SideNav } from "./SideNav.jsx";


export const UserControls = () => {
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const target = useRef(null);
    const userId = user.sub.split('|')[1];
    const username = user && user.name ? user.name : '';

    const [tradeConfig, setTradeConfig] = useState(undefined);
    const [fullTradeConfig, setFullTradeConfig] = useState(undefined);
    const [algoOptions, setAlgoOptions] = useState([]);
    const [savedAlgos, setSavedAlgos] = useState([]);
    const [selectedAlgo, setSelectedAlgo] = useState({});
    const [selectedTicker, setSelectedTicker] = useState({});

    const emptySettingsGroupForm = {
        riskSize: 500,
        maxRiskSize: 1000,
        profitTarget: 2,
        contractLimit: 20,
        dailyLossLimit: 2000,
        dailyProfitLimit: 3000,
        isTradingEnabled: false,
        stackTrades: false,
        useTrailingStop: false,
        accountsToTrade: [],
        tradeLog: [],
        fullTradeLog: [],
        isConnected: false,
        isTradingActive: false,
        accountData: [],
        activeBuid: '',
        accountStatus: [],
        activeAccountNickname: '',
        groupName: 'default',
        savedAlgos: [],
        tradingZones: [],
    };

    const defaultMasterTradeSettings = {
        isMasterTradingEnabled: true,
        isMasterAutomaticallyEnabled: true,
    };

    const [allAccounts, setAllAccounts] = useState([]);
    const [settingsGroups, setSettingsGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(
        emptySettingsGroupForm.groupName
    );
    const [showModal, setShowModal] = useState(false);
    const [isNewGroup, setIsNewGroup] = useState(false);
    const [accountStatusList, setAccountStatusList] = useState([]);
    const [connections, setConnections] = useState({});
    const [activeBuid, setActiveBuid] = useState('');
    const [tradeLogBuid, setTradeLogBuid] = useState([]);
    const [fullTradeLogBuid, setFullTradeLogBuid] = useState([]);
    const [tradeLog, setTradeLog] = useState([]);
    const [fullTradeLog, setFullTradeLog] = useState([]);
    const [showTradingZoneModal, setShowTradingZoneModal] = useState(false);
    const [isNewTradingZone, setIsNewTradingZone] = useState(true);
    const [editTradingZone, setEditTradingZone] = useState();
    const [connectionNeedsSave, setConnectionNeedsSave] = useState({});
    const [masterTradeSettings, setMasterTradeSettings] = useState(defaultMasterTradeSettings);

    const currentSettingsGroup = settingsGroups.find(
        (group) => group.groupName === selectedGroupId
    ) ?? {};

    const handleSaveGroupName = (groupName) => {
        if (isNewGroup) {
            setSettingsGroups([
                ...settingsGroups,
                {
                    ...emptySettingsGroupForm,
                    groupName: groupName,
                    accountsToTrade: tradeConfig.accountData.map((account) => {
                        return {
                            name: account.name,
                            enableTrading: false,
                            amount: account.amount,
                            isAuthorized: account.isAuthorized,
                        }
                    })
                },
            ]);
        } else {
            setSettingsGroups(
                settingsGroups.map((group) => {
                    if (group.groupName === currentSettingsGroup.groupName) {
                        return {
                            ...currentSettingsGroup,
                            groupName: groupName,
                        };
                    }
                    return group;
                })
            );
        }

        setSelectedGroupId(groupName);
        setSavedAlgos([]);
        setShowModal(false);
        setSelectedAlgo({});
        setSelectedTicker({});
    };

    const handleChange = (e) => {
        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return {
                        ...currentSettingsGroup,
                        [e.target.name]: e.target.value,
                    };
                }
                return group;
            })
        );
    };

    const handleCheckboxChange = (e) => {
        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return {
                        ...currentSettingsGroup,
                        [e.target.name]: e.target.checked,
                    };
                }
                return group;
            })
        );
    };

    const handleMasterTradingCheckboxChange = async (e) => {
        const tradeSettings = {
            ...masterTradeSettings,
            [e.target.id]: e.target.checked,
        };
        setMasterTradeSettings(tradeSettings)
        const body = { masterTradeSettings: tradeSettings };
        await updateTradeConfig(body);
    };

    const handleAccountToTradeChange = (e) => {
        const index = currentSettingsGroup.accountsToTrade.findIndex(
            (obj) => obj.name === e.target.name
        );
        const accountsToTrade = [...currentSettingsGroup.accountsToTrade];
        accountsToTrade[index].enableTrading = e.target.checked;

        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return {
                        ...currentSettingsGroup,
                        accountsToTrade,
                    };
                }
                return group;
            })
        );
    };

    useEffect(() => {
        const GetTradeConfig = async () => {
            const token = await getAccessTokenSilently();
            const headers = {
                'Authorization': `Bearer ${token}`,
            };
            const params = {
                uid: userId,
                buid: activeBuid
            };
            let urlPath = '/algo/demo/trade/config';
            try {
                let tradeConfig;
                if (!routeToServerlessAPI()) {
                    try {
                        tradeConfig = await axios.get(
                            `${process.env.REACT_APP_API_URL}${urlPath}`,
                            {
                                headers: headers,
                                params: params
                            }
                        );
                    } catch (error) {
                        if (routeToServerlessAPI(error)) {
                            urlPath = '/api/v2/trade/config';
                            tradeConfig = await axios.get(
                                `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                                {
                                    headers: headers,
                                    params: params,
                                }
                            )
                        } else {
                            throw new Error(error);
                        }
                    }
                } else {
                    urlPath = '/api/v2/trade/config';
                    tradeConfig = await axios.get(
                        `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                        {
                            headers: headers,
                            params: params,
                        }
                    )
                }


                //If maxRiskSize is null anywhere, set it to the default
                if (
                    tradeConfig.data &&
                    tradeConfig.data[activeBuid] &&
                    tradeConfig.data[activeBuid].accountGroups &&
                    tradeConfig.data[activeBuid].accountGroups.length
                ) {
                    for (const group of tradeConfig.data[activeBuid].accountGroups) {
                        if (!group.maxRiskSize) {
                            group.maxRiskSize = 1000;
                        }
                        if (!group.dailyLossLimit) {
                            group.dailyLossLimit = 2000;
                        }
                        if (!group.dailyProfitLimit) {
                            group.dailyProfitLimit = 3000;
                        }
                    }
                }
                return tradeConfig.data;
            } catch (error) {
                console.log(error.message);
                return error.message;
            }
        };

        const getTradeConfig = async () => {
            if (activeBuid) {
                const configResp = await GetTradeConfig();
                setFullTradeConfig(configResp);
                const tradeConfig = configResp[activeBuid];
                setTradeConfig(tradeConfig);
                setMasterTradeSettings(configResp.masterTradeSettings ? configResp.masterTradeSettings : defaultMasterTradeSettings);
            }
        };

        getTradeConfig();
    }, [activeBuid]);

    //useEffect for setting saved algo list
    useEffect(() => {
        if (activeBuid && tradeConfig) {
            SetAlgoList(selectedGroupId);
        }
    }, [tradeConfig, settingsGroups]);

    useEffect(() => {

        if (tradeConfig) {
            setAllAccounts(tradeConfig.accountData);
            if (tradeConfig.accountGroups?.length) {
                //Search all accounts
                for (const account of tradeConfig.accountData) {
                    for (const group of tradeConfig.accountGroups) {
                        const foundAccount = group.accountsToTrade.find((a) => a.name === account.name);
                        if (foundAccount) {
                            for (const atTrade of group.accountsToTrade) {
                                if (atTrade.name === foundAccount.name) {
                                    atTrade.amount = account.amount;
                                    atTrade.isAuthorized = account.isAuthorized;
                                }
                            }
                        } else {
                            group.accountsToTrade.push({
                                name: account.name,
                                enableTrading: false,
                                amount: account.amount,
                                isAuthorized: account.isAuthorized,
                            });
                        }
                    }
                }
            }
            setSettingsGroups(tradeConfig.accountGroups?.length ? tradeConfig.accountGroups : [emptySettingsGroupForm]);
            setSelectedGroupId(tradeConfig.accountGroups?.length ? tradeConfig.accountGroups[0].groupName : emptySettingsGroupForm.groupName);
        }
    }, [tradeConfig]);

    useEffect(() => {
        const orchestrator = async () => {
            const connections = await getConnectionStatus();
            let tempAccountStatusList = [];
            for (const userId in connections) {
                tempAccountStatusList.push({
                    name: connections[userId].name,
                    isConnected: connections[userId].isConnected,
                    id: userId
                });
            }

            setAccountStatusList(tempAccountStatusList);
            setConnections(connections);

        }
        const getConnectionStatus = async () => {
            const token = await getAccessTokenSilently();
            const status = await GetConnectionStatus(token, userId);
            return status.data;
        };
        orchestrator();
    }, [])

    useEffect(() => {
        if (accountStatusList.length) {
            const tempSettingsGroup = {
                ...currentSettingsGroup,
            };

            let tempActiveBuid = '';

            if (
                window.location.pathname.split("/").length > 2 &&
                window.location.pathname.split("/")[2]
            ) {
                tempActiveBuid = window.location.pathname.split("/")[2];
                tempSettingsGroup.activeBuid = activeBuid;
            } else {
                tempActiveBuid = accountStatusList[0].id;

                tempSettingsGroup.activeBuid = activeBuid;
            }
            tempSettingsGroup.isConnected = !!connections[activeBuid]?.isConnected;
            tempSettingsGroup.activeAccountNickname = connections[activeBuid]?.name ?? "";

            setActiveBuid(tempActiveBuid);
            setSettingsGroups(
                settingsGroups.map((group) => {
                    if (group.groupName === currentSettingsGroup.groupName) {
                        return tempSettingsGroup;
                    }
                    return group;
                })
            );
        }
    }, [accountStatusList, connections, activeBuid])

    const GetTradeLog = async (type) => {
        const token = await getAccessTokenSilently();
        const headers = {
            'Authorization': `Bearer ${token}`,
        };
        const params = {
            uid: userId,
            buid: activeBuid
        };
        try {
            let tradeLog = { data: [] };
            const mountainTime = getMountainTime(new Date());
            if (mountainTime.mountainTimeHours >= 7 && mountainTime.mountainTimeHours < 15) {
                if (type === 'full') {
                    tradeLog = await axios.get(
                        `${process.env.REACT_APP_API_URL}/algo/demo/optTradeLog`,
                        {
                            headers: headers,
                            params: params
                        }
                    );
                } else {
                    tradeLog = await axios.get(
                        `${process.env.REACT_APP_API_URL}/algo/demo/tradeLog`,
                        {
                            headers: headers,
                            params: params
                        }
                    );
                }
            }
            return tradeLog.data;
        } catch (error) {
            //console.log(error.message);
            return error.message;
        }
    };

    const getTradeLog = async (type) => {
        const resp = await GetTradeLog(type);
        const tradeLog = resp[activeBuid];
        if (tradeLog && Object.keys(tradeLog).length) {
            if (type === 'full') {
                setFullTradeLogBuid(tradeLog);
            } else {
                setTradeLogBuid(tradeLog);
            }
        }
    };

    useEffect(() => {
        //getTradeLog('full');
        getTradeLog('personal');
    }, [activeBuid]);

    useEffect(() => {
        if (tradeLogBuid && tradeLogBuid[selectedGroupId]) {

            setTradeLog(tradeLogBuid[selectedGroupId]);
            setFullTradeLog(fullTradeLogBuid[selectedGroupId]);
        }
    }, [tradeLogBuid, selectedGroupId]);

    useEffect(() => {
        // fetch from api and set state
        const getAlgoConfig = async () => {
            const token = await getAccessTokenSilently();
            const headers = {
                'Authorization': `Bearer ${token}`,
            };
            const params = {
                uid: userId,
                buid: activeBuid
            };
            let urlPath = '/algo/config';
            try {
                let algoConfigResp;
                if (!routeToServerlessAPI()) {
                    try {
                        algoConfigResp = await axios.get(
                            `${process.env.REACT_APP_API_URL}${urlPath}`,
                            {
                                headers: headers,
                                params: params
                            }
                        );
                    } catch (error) {
                        if (routeToServerlessAPI(error)) {
                            urlPath = '/api/v2/algo/config';
                            algoConfigResp = await axios.get(
                                `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                                {
                                    headers: headers,
                                    params: params,
                                }
                            )
                        } else {
                            throw new Error(error);
                        }
                    }
                } else {
                    urlPath = '/api/v2/algo/config';
                    algoConfigResp = await axios.get(
                        `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                        {
                            headers: headers,
                            params: params,
                        }
                    )
                }

                setAlgoOptions(algoConfigResp.data);
            } catch (error) {
                console.log(error.message);
            }
        }
        if (!algoOptions || algoOptions.length === 0) {
            getAlgoConfig();
        }
    }, []);

    useEffect(() => {
        if (savedAlgos && savedAlgos.length) {
            currentSettingsGroup.savedAlgos = savedAlgos.map((algo) => {
                return algo.id;
            });
        }
    }, [savedAlgos]);

    const handleAlgoSelection = (algoName) => {
        if (algoName) {
            const algo = algoOptions.find((algo) => algo.algoName === algoName);
            if (algo) {
                setSelectedAlgo(algo);
                setSelectedTicker({});
            }
        }
    };

    const handleTickerSelection = (tickerName) => {
        if (tickerName && selectedAlgo && selectedAlgo.tickers) {
            setSelectedTicker({
                tickerName,
                timeframes: selectedAlgo.tickers[tickerName].timeframes.filter((timeframe) => {
                    if (savedAlgos.find((algo) => algo.id === `${selectedAlgo.algoName}_${tickerName}_${timeframe}`)) {
                        return false;
                    }
                    return true;
                }),
            });
        }
    };

    const sortAlgos = (algosList, sortByList) => {
        for (const prop of sortByList) {
            algosList.sort((a, b) => {
                if (a[prop] > b[prop]) {
                    return 1;
                }
                if (a[prop] < b[prop]) {
                    return -1;
                }
                return 0;
            });
        }
        return algosList;
    };

    const handleTimeFrameSelection = (selectedTimeFrame) => {
        if (selectedTimeFrame) {
            const tempAlgoOptions = [...algoOptions];

            setAlgoOptions(
                tempAlgoOptions.map((algoOption) => {
                    if (algoOption.algoName === selectedAlgo.algoName) {
                        // algoOption.tickers[selectedTicker.tickerName].timeframes =
                        //     algoOption.tickers[selectedTicker.tickerName].timeframes.filter(
                        //         (timeframe) => !!(timeframe !== selectedTimeFrame && !savedAlgos.find((algo) => algo.id === `${algoOption.algoName}_${selectedTicker.tickerName}_${timeframe}`))
                        //     );

                        setSelectedTicker({
                            tickerName: selectedTicker.tickerName,
                            timeframes:
                                algoOption.tickers[selectedTicker.tickerName].timeframes,
                        });
                    }

                    return algoOption;
                })
            );

            const algoId = `${selectedAlgo.algoName}_${selectedTicker.tickerName}_${selectedTimeFrame}`;
            const algo = savedAlgos.find((algo) => algo.id === algoId);

            if (!algo) {
                const savedList = [
                    ...savedAlgos,
                    {
                        id: algoId,
                        name: selectedAlgo.algoName,
                        ticker: selectedTicker.tickerName,
                        timeframe: selectedTimeFrame,
                        new: true,
                    },
                ];

                sortAlgos(savedList, ["timeframe", "ticker", "name"]);

                setSavedAlgos(savedList);
            }
        }
    };

    const handleRemoveSelection = (removedAlgo) => {
        setSavedAlgos(
            savedAlgos.filter((savedAlgo) => savedAlgo.id !== removedAlgo.id)
        );

        const tempAlgoOptions = [...algoOptions];
        setAlgoOptions(
            tempAlgoOptions.map((algoOption) => {
                if (algoOption.algoName === removedAlgo.name) {
                    if (
                        !algoOption.tickers[removedAlgo.ticker].timeframes.includes(
                            removedAlgo.timeframe
                        )
                    ) {
                        algoOption.tickers[removedAlgo.ticker].timeframes.push(
                            removedAlgo.timeframe
                        );
                        algoOption.tickers[removedAlgo.ticker].timeframes.sort((a, b) => {
                            if (a > b) {
                                return 1;
                            }
                            if (a < b) {
                                return -1;
                            }
                            return 0;
                        });
                    }
                }

                return algoOption;
            })
        );
    };

    const ClearForm = (e) => {
        const clearedSettingsForm = {
            ...currentSettingsGroup,
            riskSize: 500,
            maxRiskSize: 1000,
            dailyLossLimit: 2000,
            dailyProfitLimit: 3000,
            isTradingEnabled: false,
            stackTrades: false,
            useTrailingStop: false,
            savedAlgos: []
        };

        if (clearedSettingsForm.accountsToTrade) {
            let newAccounts = [...clearedSettingsForm.accountsToTrade];
            for (const account of newAccounts) {
                account.enableTrading = false;
            }
            clearedSettingsForm.accountsToTrade = newAccounts;
        }

        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return clearedSettingsForm;
                }
                return group;
            })
        );
    };

    const ActiveTradingStatus = () => {
        if (
            currentSettingsGroup.isTradingActive &&
            connections[activeBuid]?.isConnected &&
            masterTradeSettings.isMasterTradingEnabled
        ) {
            return (
                <Badge className="mb-3" bg="success">
                    Trading Is Active
                </Badge>
            );
        } else {
            return (
                <div>
                    <Badge className="mb-3" bg="danger">
                        Trading Is Not Active
                    </Badge>
                    {InfoHover(
                        "",
                        `To start trading, first, you must be connected to your
                                brokerage. Then, in your trade settings, you must select at least one algorithm, set a risk
                                size, toggle "Turn Trading On and Off" to the "On" position, and
                                have at least one "Accounts to Trade On" account toggled to the
                                "On" position.`
                    )}
                </div>
            );
        }
    }

    const SetAlgoList = (groupId) => {
        if (settingsGroups && settingsGroups.length) {
            const savedAlgos = [];
            for (const group of settingsGroups) {
                if (group.groupName === groupId) {
                    for (const algo of group.savedAlgos) {
                        const splitId = algo.split('_');
                        const [name, ticker, timeframe] = splitId;
                        savedAlgos.push(
                            {
                                id: algo,
                                name,
                                ticker,
                                timeframe
                            }
                        )
                    }
                }
            }
            setSavedAlgos(sortAlgos(savedAlgos, ["timeframe", "ticker", "name"]));
        } else {
            setSavedAlgos([]);
        }
    }

    const SubmitTradeConfig = async (e) => {
        e.preventDefault();
        const token = await getAccessTokenSilently();

        for (const buid in fullTradeConfig) {
            if (connectionNeedsSave[buid] || buid === activeBuid) {
                let connectionTradeConfig = tradeConfig;
                let connectionSettingsGroups = settingsGroups;
                if (buid !== activeBuid) {
                    connectionTradeConfig = fullTradeConfig[buid];
                    connectionSettingsGroups = fullTradeConfig[buid].accountGroups;
                }
                const body = {
                    accountData: connectionTradeConfig.accountData.map((account) => {
                        return {
                            name: account.name,
                            amount: account.amount,
                            id: account.id,
                        }
                    }),
                    accountGroups: connectionSettingsGroups.map((group) => {
                        return {
                            accountsToTrade: activeBuid !== buid ? group.accountsToTrade : group.accountsToTrade.filter((a) => a.amount !== undefined && typeof a.amount === 'number' && a.enableTrading)
                                .map((account) => {
                                    return {
                                        name: account.name,
                                        enableTrading: account.enableTrading,
                                    }
                                }),
                            contractLimit: group.contractLimit,
                            dailyLossLimit: Number(group.dailyLossLimit),
                            dailyProfitLimit: Number(group.dailyProfitLimit),
                            groupName: group.groupName,
                            isTradingEnabled: group.isTradingEnabled,
                            profitTarget: group.profitTarget,
                            riskSize: Number(group.riskSize),
                            maxRiskSize: Number(group.maxRiskSize),
                            savedAlgos: group.savedAlgos,
                            stackTrades: group.stackTrades,
                            useTrailingStop: group.useTrailingStop,
                            tradingZones: group.tradingZones,
                        }
                    })
                }
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };
                const params = {
                    uid: userId,
                    buid: buid,
                };
                let urlPath = '/algo/demo/trade/config';
                try {
                    let newTradeConfig;
                    if (!routeToServerlessAPI()) {
                        try {
                            newTradeConfig = await axios.post(
                                `${process.env.REACT_APP_API_URL}${urlPath}`,
                                body,
                                {
                                    headers: headers,
                                    params: params
                                }
                            );
                        } catch (error) {
                            if (routeToServerlessAPI(error)) {
                                urlPath = '/api/v2/trade/config';
                                newTradeConfig = await axios.post(
                                    `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                                    body,
                                    {
                                        headers: headers,
                                        params: params,
                                    }
                                )
                            } else {
                                throw new Error(error);
                            }
                        }
                    } else {
                        urlPath = '/api/v2/trade/config';
                        newTradeConfig = await axios.post(
                            `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                            body,
                            {
                                headers: headers,
                                params: params,
                            }
                        )
                    }
                    if (buid === activeBuid) {
                        setSettingsGroups(
                            settingsGroups.map((group) => {
                                for (const tfGroup of newTradeConfig.data.accountGroups) {
                                    if (group.groupName === tfGroup.groupName) {
                                        return {
                                            ...group,
                                            isTradingActive: tfGroup.isTradingActive,
                                        };
                                    }
                                }
                                return group;
                            })
                        );
                    }
                    const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
                    needsSave[buid] = false;
                    setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
                } catch (error) {
                    console.log(error.message);
                    if (error && error.response && error.response.data) {
                        alert(`Error Submitting Form. ${error.response.data}`);
                    } else {
                        alert("Error Submitting Form. Try Again");
                    }
                    return error.message;
                }
            }
        }

        alert("Form Submitted Successfully");

    };

    const updateTradeConfig = async (body) => {
        const token = await getAccessTokenSilently();
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        const params = {
            uid: userId,
            buid: activeBuid,
        };
        let urlPath = '/algo/demo/trade/config';
        try {
            if (!routeToServerlessAPI()) {
                try {
                    await axios.put(
                        `${process.env.REACT_APP_API_URL}${urlPath}`,
                        body,
                        {
                            headers: headers,
                            params: params
                        }
                    );
                } catch (error) {
                    if (routeToServerlessAPI(error)) {
                        urlPath = '/api/v2/trade/config';
                        await axios.put(
                            `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                            body,
                            {
                                headers: headers,
                                params: params,
                            }
                        )
                    } else {
                        throw new Error(error);
                    }
                }
            } else {
                urlPath = '/api/v2/trade/config';
                await axios.put(
                    `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                    body,
                    {
                        headers: headers,
                        params: params,
                    }
                )
            }
        } catch (error) {
            console.log(error.message);
            if (error && error.response && error.response.data) {
                alert(`Error saving. ${error.response.data}`);
            } else {
                alert("Error saving. Try again");
            }
            return error.message;
        }

        alert("Saved Successfully");

    };

    const liquidate = async () => {
        const token = await getAccessTokenSilently();
        const headers = {
            'Authorization': `Bearer ${token}`,
        };
        const params = {
            uid: userId,
            buid: activeBuid,
        };

        try {
            let urlPath = '/algo/liquidate';
            if (!routeToServerlessAPI()) {
                try {
                    await axios.get(
                        `${process.env.REACT_APP_API_URL}${urlPath}`,
                        {
                            headers: headers,
                            params: params
                        }
                    );
                } catch (error) {
                    if (routeToServerlessAPI(error)) {
                        urlPath = '/api/v2/algo/liquidate';
                        await axios.get(
                            `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                            {
                                headers: headers,
                                params: params,
                            }
                        )
                    } else {
                        throw new Error(error);
                    }
                }
            } else {
                urlPath = '/api/v2/algo/liquidate';
                await axios.get(
                    `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                    {
                        headers: headers,
                        params: params,
                    }
                )
            }

            alert("Successfully liquidated");
        } catch (error) {
            console.log(error.message);
            alert("Error liquidating. Try Again");
            return error.message;
        }
    };

    const handleOpenTradingZoneModal = () => {
        setIsNewTradingZone(true);
        setEditTradingZone(undefined);
        setShowTradingZoneModal(true);
    }

    const handleOpenTradingZoneEditModal = (zone) => {
        setIsNewTradingZone(false);
        setEditTradingZone(zone);
        setShowTradingZoneModal(true);
    }

    const handleAddASettingsGroup = () => {
        setShowModal(true);
        setIsNewGroup(true);
    };

    const handleEditSettingsGroupName = () => {
        setShowModal(true);
        setIsNewGroup(false);
    };

    const handleDeleteGroup = (groupName) => {
        const filteredGroups = settingsGroups.filter(
            (group) => group.groupName !== groupName
        );
        setSettingsGroups(filteredGroups);
        setSelectedGroupId(filteredGroups[0].groupName);
        SetAlgoList(filteredGroups[0].groupName);
    };

    const handleGroupSelection = (event) => {
        const { target } = event;
        const selectedGroupId = target.value;
        setSelectedGroupId(selectedGroupId);
        SetAlgoList(selectedGroupId);
        setSelectedAlgo({});
        setSelectedTicker({});
    };

    const AccountsToTrade = ({ accounts }) => {
        let accountsList;
        if (accounts) {
            accounts.sort((a, b) => {
                if (a.name < b.name) {
                    return 1;
                }
                if (a.name > b.name) {
                    return -1;
                }
                return 0;
            });
            return (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Account</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            accounts.filter((a) => a.amount !== undefined && typeof a.amount === 'number')
                                .map((account) => {
                                    return (
                                        <tr key={account.name}>
                                            <td>
                                                <Form.Check
                                                    disabled={!account.isAuthorized}
                                                    type="switch"
                                                    id={account.name}
                                                    label={account.name}
                                                    name={account.name}
                                                    checked={account.enableTrading}
                                                    onChange={handleAccountToTradeChange}
                                                    value={account.enableTrading}
                                                />
                                            </td>
                                            <td>${Math.round(account.amount * 100) / 100}</td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                </Table>
            )

        } else {
            accountsList = <Form.Check></Form.Check>;
        }

        return accountsList;
    };

    const TradeTable = () => {
        let wins = 0;
        let losses = 0;
        let profit = 0;
        const testArray = tradeLog ?? [];
        for (let trade of testArray) {
            let pricePerPoint;
            if (trade.ticker) {
                pricePerPoint = config.symbols[trade.ticker.substring(0, trade.ticker.length - 2)].pricePerPoint;
            } else {
                pricePerPoint = config.symbols.ES.pricePerPoint;
            }
            if (trade.status === 'Win' && trade.filled) {
                wins = wins + 1;
                if (trade.entryStyle === 'sma' && trade.lastPrice) {
                    profit = profit + Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * Math.abs(trade.lastPrice - trade.fillPrice);
                } else {
                    profit = profit + Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * trade.profitTarget;
                }

            } else if (trade.status === 'Loss') {
                losses = losses + 1;
                if (trade.entryStyle === 'sma' && trade.lastPrice) {
                    profit = profit - Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * Math.abs(trade.lastPrice - trade.fillPrice);
                } else {
                    profit = profit - Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * trade.stopSize;
                }

            }
        }

        return (
            <div>
                {/* <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Total Trades</th>
                            <th>Total Trades Filled</th>
                            <th>Wins</th>
                            <th>Losses</th>
                            <th>Win Percentage</th>
                            <th>Estimated Profit</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{tradeLog.length ?? 0}</td>
                            <td>{wins + losses}</td>
                            <td>{wins}</td>
                            <td>{losses}</td>
                            <td>{(wins / (wins + losses) * 100) || 0}%</td>
                            <td>${Math.round(profit) || 0}</td>
                        </tr>
                    </tbody>
                </Table> */}
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Detection Time</th>
                            {/* <th>Filled Time</th> */}
                            {/* <th>End Time</th> */}
                            <th>Trade Type</th>
                            <th>Fill Price</th>
                            <th>Stop Size</th>
                            <th>Profit Target</th>
                            {/* <th>Trade Status</th> */}
                            <th>Algorithm</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            tradeLog ? tradeLog.map(trade => {
                                return (
                                    <tr key={trade.timestamp + trade.fillPrice}>
                                        <td>{new Date(trade.timestamp).toString()}</td>
                                        {/* <td>{trade.filledTimestamp ? new Date(trade.filledTimestamp).toString() : 'N/A'}</td> */}
                                        {/* <td>{trade.endTimestamp ? new Date(trade.endTimestamp).toString() : 'N/A'}</td> */}
                                        <td>{trade.type.includes('BUY') ? 'BUY' : 'SELL'}</td>
                                        <td>{Math.round(trade.fillPrice * 100) / 100}</td>
                                        <td>{Math.round(trade.stopSize * 100) / 100}</td>
                                        <td>{Math.round(trade.profitTarget * 100) / 100}</td>
                                        {/* <td>{trade.status}</td> */}
                                        <td>{trade.algoName + ' - ' + trade.ticker + ' - ' + trade.timeframe}</td>
                                    </tr>
                                );
                            }) : []
                        }
                    </tbody>
                </Table>
            </div>
        )
    };

    const AccountDropdown = () => {
        return (
            <Dropdown as={ButtonGroup} size="sm">
                <Dropdown.Toggle variant="primary" id="dropdown-basic">
                    {connections[activeBuid]?.name ? connections[activeBuid]?.name + ' ' : ''}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {
                        accountStatusList && accountStatusList.length ? accountStatusList.map(account => {
                            return (
                                <Dropdown.Item
                                    key={account.id}
                                    href={`/trade/${account.id}`}
                                >
                                    {account.name}
                                </Dropdown.Item>
                            );
                        }) : null
                    }
                </Dropdown.Menu>
            </Dropdown>
        )
    };

    const addConnectionTradingZones = (tradeConfigGroups, groupList, tradingZone, buid) => {
        let isSaved = false;
        const updatedSettingsGroups = tradeConfigGroups.map((group) => {
            if (
                groupList.findIndex((g) =>
                    g.groupName === group.groupName &&
                    g.buid === buid &&
                    g.isCopiedToGroup
                ) >= 0
            ) {
                if (!group.tradingZones) {
                    group.tradingZones = [];
                }
                if (group.tradingZones.filter((zone) => zone.id === tradingZone.id).length === 0) {
                    group.tradingZones.push(tradingZone);
                    isSaved = true;
                } else {
                    alert(`Trading zone already exists for group ${group.groupName}`);
                }

                return group;
            }
            return group;
        });
        if (isSaved) {
            const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
            needsSave[buid] = true;
            setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
        }

        return updatedSettingsGroups;
    }

    const editConnectionTradingZones = (tradeConfigGroups, groupList, tradingZone, buid) => {
        const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
        const updatedSettingsGroups = tradeConfigGroups.map((group) => {
            const groupListIndex = groupList.findIndex((g) =>
                g.groupName === group.groupName &&
                g.buid === buid
            );
            if (groupListIndex >= 0) {
                if (!group.tradingZones) {
                    group.tradingZones = [];
                }
                const editZoneIndex = group.tradingZones.findIndex((zone) => zone.id === tradingZone.id);
                if (editZoneIndex >= 0) {
                    //If it's still in the group
                    if (groupList[groupListIndex].isCopiedToGroup) {
                        group.tradingZones[editZoneIndex] = tradingZone;
                        needsSave[buid] = true;

                    } else {
                        //If it's no longer in the group remove the zone
                        group.tradingZones.splice(editZoneIndex, 1);
                        needsSave[buid] = true;
                    }
                } else if (editZoneIndex < 0 && groupList[groupListIndex].isCopiedToGroup) {
                    //Add the zone to the group
                    group.tradingZones.push(tradingZone);
                    needsSave[buid] = true;

                }
                return group;
            }
            return group;
        });
        setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
        return updatedSettingsGroups;
    }

    const handleAddTradingZone = (tradingZone, groupList) => {
        setIsNewTradingZone(true);
        tradingZone.id = new Date().getTime();
        if (tradingZone.min && tradingZone.max && tradingZone.validThrough) {
            tradingZone.min = parseFloat(tradingZone.min);
            tradingZone.max = parseFloat(tradingZone.max);
            tradingZone.validThrough = parseFloat(tradingZone.validThrough);
            tradingZone.createdDate = new Date();
        }
        let validZone = true;
        //Validation of the zone
        if (
            tradingZone.min &&
            tradingZone.max &&
            tradingZone.type &&
            tradingZone.ticker &&
            tradingZone.min < tradingZone.max &&
            groupList.filter((group) => group.isCopiedToGroup).length
        ) {
            const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));
            for (const buid in allTradeConfig) {
                //Update each connection's groups' trading zones
                if (allTradeConfig[buid] && allTradeConfig[buid].accountGroups && allTradeConfig[buid].accountGroups.length) {
                    allTradeConfig[buid].accountGroups = addConnectionTradingZones(allTradeConfig[buid].accountGroups, groupList, tradingZone, buid);
                }
            }
            setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
            setFullTradeConfig(allTradeConfig);
            //setTradeConfig(allTradeConfig[activeBuid]);

        } else {
            validZone = false;
            alert('Invalid trading zone. All inputs are required, the minimum price has to be less than the maximum price, and at least one group has to be selected.');
        }

        if (validZone) {
            setShowTradingZoneModal(false);
        }
    };

    const handleEditTradingZone = (tradingZone, groupList) => {
        if (tradingZone.min && tradingZone.max && tradingZone.validThrough) {
            tradingZone.min = parseFloat(tradingZone.min);
            tradingZone.max = parseFloat(tradingZone.max);
            tradingZone.validThrough = parseFloat(tradingZone.validThrough);
        }
        let validZone = true;
        //Validation of the zone
        if (
            tradingZone.min &&
            tradingZone.max &&
            tradingZone.type &&
            tradingZone.ticker &&
            tradingZone.min < tradingZone.max &&
            groupList.filter((group) => group.isCopiedToGroup).length
        ) {
            const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));
            for (const buid in allTradeConfig) {
                //Update each connection's groups' trading zones
                if (allTradeConfig[buid] && allTradeConfig[buid].accountGroups && allTradeConfig[buid].accountGroups.length) {
                    allTradeConfig[buid].accountGroups = editConnectionTradingZones(allTradeConfig[buid].accountGroups, groupList, tradingZone, buid);
                }
            }
            setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
            setFullTradeConfig(allTradeConfig);
            //setTradeConfig(allTradeConfig[activeBuid]);
        } else {
            validZone = false;
            alert('Invalid trading zone. All inputs are required, the minimum price has to be less than the maximum price, and at least one group has to be selected.');
        }

        if (validZone) {
            setShowTradingZoneModal(false);
        }
    };

    const handleDeleteTradingZone = (tradingZone) => {
        const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));
        const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
        for (const buid in allTradeConfig) {
            //Update each connection's groups' trading zones
            if (allTradeConfig[buid] && allTradeConfig[buid].accountGroups && allTradeConfig[buid].accountGroups.length) {
                for (const group of allTradeConfig[buid].accountGroups) {
                    if (group.tradingZones && group.tradingZones.length) {
                        //Filter out the trading zone being deleted
                        group.tradingZones = group.tradingZones
                            .filter((zone) => zone.id !== tradingZone.id);
                        needsSave[buid] = true;
                    }
                }
            }
        }
        setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
        setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
        setFullTradeConfig(allTradeConfig);
        //setTradeConfig(allTradeConfig[activeBuid]);
    };

    return (
        <>
            {
                isAuthorized('tradeScreen') && (
                    <>
                        {showModal && (
                            <GroupNameModal
                                isNewGroup={isNewGroup}
                                showModal={showModal}
                                setShowModal={setShowModal}
                                handleSaveGroupName={handleSaveGroupName}
                                initialGroupName={isNewGroup ? "" : currentSettingsGroup.groupName}
                                groups={settingsGroups}
                            />
                        )}
                        {showTradingZoneModal && (
                            <TradingZoneModal
                                isNewTradingZone={isNewTradingZone}
                                showModal={showTradingZoneModal}
                                setShowModal={setShowTradingZoneModal}
                                handleTradingZone={isNewTradingZone ? handleAddTradingZone : handleEditTradingZone}
                                existingTradingZone={editTradingZone}
                                tradeConfig={fullTradeConfig}
                                connections={connections}
                                userId={userId}
                            />
                        )}
                        {
                            !settingsGroups.length ? (<></>) : (
                                <div>
                                    <Container >
                                        <Row>
                                            <Col>
                                                {/* <SideNav
                                                    connections={connections}
                                                    tradeConfig={fullTradeConfig}
                                                /> */}
                                            </Col>
                                            <Col xs={10} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left">
                                                <Row className="align-items-center border-bottom mb-2 pb-2">
                                                    <Col>
                                                        <h3>{connections[activeBuid]?.isConnected ? 'Connection:' : ''} <AccountDropdown /></h3>
                                                    </Col>
                                                    <Col className="d-flex justify-content-end">
                                                        <Form.Group>
                                                            <Form.Check
                                                                size="lg"
                                                                id="isMasterTradingEnabled"
                                                                type="switch"
                                                                label={InfoHover(
                                                                    "Master On/Off Switch",
                                                                    "When this setting is turned on, any groups that have trading activated will be allowed to execute orders. When it's off, all trading will be turned off across all connections and all groups."
                                                                )}
                                                                key="isMasterTradingEnabled"
                                                                name="isMasterTradingEnabled"
                                                                checked={masterTradeSettings.isMasterTradingEnabled}
                                                                onChange={handleMasterTradingCheckboxChange}
                                                                value={masterTradeSettings.isMasterTradingEnabled}
                                                            />
                                                            <Form.Check
                                                                size="lg"
                                                                id="isMasterAutomaticallyEnabled"
                                                                type="switch"
                                                                label={InfoHover(
                                                                    "Enable Tomorrow",
                                                                    "When activated, the Master On/Off Switch will turn on automatically at the beginning of each trading day."
                                                                )}
                                                                key="isMasterAutomaticallyEnabled"
                                                                name="isMasterAutomaticallyEnabled"
                                                                checked={masterTradeSettings.isMasterAutomaticallyEnabled}
                                                                onChange={handleMasterTradingCheckboxChange}
                                                                value={masterTradeSettings.isMasterAutomaticallyEnabled}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Row className="border-bottom">
                                                    <Col className="d-flex flex-start align-items-center mb-2">
                                                        <Form.Label>
                                                            <h5
                                                                style={{
                                                                    margin: 0
                                                                }}
                                                            >
                                                                Group:
                                                            </h5>
                                                        </Form.Label>
                                                        <Form.Group
                                                            controlId="group-select"
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "flex-start",
                                                                width: "30%",
                                                                marginRight: "10px",
                                                                marginLeft: "10px"
                                                            }}
                                                        >
                                                            <Form.Select
                                                                onChange={handleGroupSelection}
                                                                value={selectedGroupId}
                                                            >
                                                                {settingsGroups.map((group) => (
                                                                    <option key={group.groupName} value={group.groupName}>
                                                                        {group.groupName}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Dropdown>
                                                            <Dropdown.Toggle variant="primary" id="dropdown-basic" size="sm">
                                                                Group Actions
                                                            </Dropdown.Toggle>

                                                            <Dropdown.Menu>
                                                                <Dropdown.Item
                                                                    key="add-group"
                                                                    variant="success"
                                                                    onClick={() => handleAddASettingsGroup()}
                                                                >
                                                                    Create A Group
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    key="edit-group"
                                                                    variant="secondary"
                                                                    onClick={handleEditSettingsGroupName}>
                                                                    Edit Group Name
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    key="delete-group"
                                                                    disabled={settingsGroups.length < 2}
                                                                    variant="outline-danger"
                                                                    ref={target}
                                                                    onClick={() =>
                                                                        handleDeleteGroup(currentSettingsGroup.groupName)
                                                                    }
                                                                >
                                                                    Delete Group
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </Col >
                                                </Row >
                                                <ActiveTradingStatus />
                                                {
                                                    connections[activeBuid]?.isConnected && (
                                                        <Form className="bg-light">
                                                            <Row className="mb-3 pb-2 border-bottom">
                                                                <Col>
                                                                    <Accordion>
                                                                        <Accordion.Item eventKey="0">
                                                                            <Accordion.Header>
                                                                                <Form.Label>
                                                                                    {InfoHover(
                                                                                        "Select Algorithms",
                                                                                        "Select the algorithm(s) you want to take trades on. Talk to your licensor for more details on each algorithm."
                                                                                    )}
                                                                                </Form.Label>
                                                                            </Accordion.Header>
                                                                            <Accordion.Body>
                                                                                <Container
                                                                                    className="d-flex justify-content-center"
                                                                                    style={{ padding: "0" }}
                                                                                >
                                                                                    <Container style={{ padding: "2px" }}>
                                                                                        <h5>Algorithm</h5>
                                                                                        <ListGroup
                                                                                            id="algos"
                                                                                            style={{
                                                                                                height: "200px",
                                                                                                overflowY: "auto",
                                                                                                borderRadius: "0",
                                                                                                border: "1px solid lightgray",
                                                                                            }}
                                                                                        >
                                                                                            {algoOptions.map((algo) => (
                                                                                                <ListGroup.Item
                                                                                                    key={algo.algoName}
                                                                                                    active={
                                                                                                        selectedAlgo.algoName === algo.algoName
                                                                                                    }
                                                                                                    style={{
                                                                                                        borderLeft: "none",
                                                                                                        borderRight: "none",
                                                                                                        borderRadius: "0",
                                                                                                        cursor: "pointer",
                                                                                                    }}
                                                                                                    onClick={() =>
                                                                                                        handleAlgoSelection(algo.algoName)
                                                                                                    }
                                                                                                >
                                                                                                    {algo.testing ? algo.algoName + ' BETA' : algo.algoName}
                                                                                                </ListGroup.Item>
                                                                                            ))}
                                                                                        </ListGroup>
                                                                                    </Container>
                                                                                    <Container style={{ padding: "2px" }}>
                                                                                        <h5>Ticker</h5>
                                                                                        <ListGroup
                                                                                            id="tickers"
                                                                                            style={{
                                                                                                height: "200px",
                                                                                                overflowY: "auto",
                                                                                                borderRadius: "0",
                                                                                                border: "1px solid lightgray",
                                                                                            }}
                                                                                        >
                                                                                            {selectedAlgo.tickers &&
                                                                                                Object.keys(selectedAlgo.tickers).map(
                                                                                                    (tickerName) => {
                                                                                                        return (
                                                                                                            <ListGroup.Item
                                                                                                                key={tickerName}
                                                                                                                active={
                                                                                                                    selectedTicker.tickerName ===
                                                                                                                    tickerName
                                                                                                                }
                                                                                                                style={{
                                                                                                                    borderLeft: "none",
                                                                                                                    borderRight: "none",
                                                                                                                    borderRadius: "0",
                                                                                                                    cursor: "pointer",
                                                                                                                }}
                                                                                                                onClick={() =>
                                                                                                                    handleTickerSelection(tickerName)
                                                                                                                }
                                                                                                            >
                                                                                                                {tickerName}
                                                                                                            </ListGroup.Item>
                                                                                                        );
                                                                                                    }
                                                                                                )}
                                                                                        </ListGroup>
                                                                                    </Container>
                                                                                    <Container style={{ padding: "2px" }}>
                                                                                        <h5>Time Frame</h5>
                                                                                        <ListGroup
                                                                                            id="time-frames"
                                                                                            style={{
                                                                                                height: "200px",
                                                                                                overflowY: "auto",
                                                                                                borderRadius: "0",
                                                                                                border: "1px solid lightgray",
                                                                                            }}
                                                                                        >
                                                                                            {selectedTicker.timeframes &&
                                                                                                selectedTicker.timeframes.map((timeframe) => {
                                                                                                    const id = `${selectedAlgo.algoName}_${selectedTicker.tickerName}_${timeframe}`;
                                                                                                    const foundAlgo = savedAlgos.find(
                                                                                                        (algo) => algo.id === id
                                                                                                    );
                                                                                                    return (
                                                                                                        <Fragment key={id}>
                                                                                                            {!foundAlgo ? (
                                                                                                                <ListGroup.Item
                                                                                                                    style={{
                                                                                                                        display: "flex",
                                                                                                                        justifyContent: "space-between",
                                                                                                                        borderLeft: "none",
                                                                                                                        borderRight: "none",
                                                                                                                        borderRadius: "0",
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {timeframe}
                                                                                                                    <Button
                                                                                                                        variant="outline-primary"
                                                                                                                        size="sm"
                                                                                                                        onClick={() =>
                                                                                                                            handleTimeFrameSelection(
                                                                                                                                timeframe
                                                                                                                            )
                                                                                                                        }
                                                                                                                    >
                                                                                                                        +
                                                                                                                    </Button>
                                                                                                                </ListGroup.Item>
                                                                                                            ) : (
                                                                                                                <></>
                                                                                                            )}
                                                                                                        </Fragment>
                                                                                                    );
                                                                                                })}
                                                                                        </ListGroup>
                                                                                    </Container>
                                                                                </Container>
                                                                                <Container style={{ padding: "16px 0" }}>
                                                                                    <h5>Saved Algorithms</h5>
                                                                                    <div
                                                                                        style={{
                                                                                            height: "300px",
                                                                                            overflowY: "auto",
                                                                                            border: "1px solid #ccc",
                                                                                            background: "white",
                                                                                            borderRadius: "5px",
                                                                                            padding: "5px",
                                                                                        }}
                                                                                    >
                                                                                        <Table striped bordered hover>
                                                                                            <thead>
                                                                                                <tr>
                                                                                                    <th>Algorithm</th>
                                                                                                    <th>Ticker</th>
                                                                                                    <th>Time Frame</th>
                                                                                                    <th>Actions</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {savedAlgos.map((algo) => {
                                                                                                    return (
                                                                                                        <tr key={algo.name + algo.ticker + algo.timeframe}>
                                                                                                            <td>
                                                                                                                {" "}
                                                                                                                {algo.name}{" "}
                                                                                                                {algo.new ? (
                                                                                                                    <span
                                                                                                                        style={{
                                                                                                                            color: "green",
                                                                                                                            fontWeight: "bold",
                                                                                                                            fontSize: "10px",
                                                                                                                            verticalAlign: "top",
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        NEW
                                                                                                                    </span>
                                                                                                                ) : (
                                                                                                                    <></>
                                                                                                                )}
                                                                                                            </td>
                                                                                                            <td>{algo.ticker}</td>
                                                                                                            <td>{algo.timeframe}</td>
                                                                                                            <td>
                                                                                                                <Button
                                                                                                                    variant="outline-danger"
                                                                                                                    size="sm"
                                                                                                                    onClick={() =>
                                                                                                                        handleRemoveSelection(algo)
                                                                                                                    }
                                                                                                                >
                                                                                                                    Remove
                                                                                                                </Button>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    );
                                                                                                })}
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </div>
                                                                                </Container>
                                                                            </Accordion.Body>
                                                                        </Accordion.Item>
                                                                    </Accordion>
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-3 border-bottom">
                                                                <Col>
                                                                    <Form.Group className="mb-3 pb-2 border-bottom" controlId="tradeSettings">
                                                                        <Form.Label>Trading Settings</Form.Label>
                                                                        <Form.Check
                                                                            id="isTradingEnabled"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Turn Trading On and Off",
                                                                                "When this setting is turned on, new trades will be executed against the accounts you have toggled on if you are in a trading time window. Toggle this switch off to turn trading off."
                                                                            )}
                                                                            key="isTradingEnabled"
                                                                            name="isTradingEnabled"
                                                                            checked={currentSettingsGroup.isTradingEnabled}
                                                                            onChange={handleCheckboxChange}
                                                                            value={currentSettingsGroup.isTradingEnabled}
                                                                        />
                                                                        <Form.Check
                                                                            id="stackTrades"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Stack Trades",
                                                                                "Stacking trades allows you to take another trade while a trade is currently working. If Stack Trades is turned off, you will be limited to 1 trade working at a time."
                                                                            )}
                                                                            key="stackTrades"
                                                                            name="stackTrades"
                                                                            checked={currentSettingsGroup.stackTrades}
                                                                            onChange={handleCheckboxChange}
                                                                            value={currentSettingsGroup.stackTrades}
                                                                        />
                                                                        <Form.Check
                                                                            id="useTrailingStop"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Trailing Stop",
                                                                                "When toggled on, an order with an auto-trailing stop will be placed."
                                                                            )}
                                                                            key="useTrailingStop"
                                                                            name="useTrailingStop"
                                                                            checked={currentSettingsGroup.useTrailingStop}
                                                                            onChange={handleCheckboxChange}
                                                                            value={currentSettingsGroup.useTrailingStop}
                                                                        />
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-3 pb-2 border-bottom" controlId="accountToTrade">
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Accounts to Trade On",
                                                                                "Choose which accounts to execute new trades on."
                                                                            )}
                                                                        </Form.Label>
                                                                        <div style={{
                                                                            height: "200px",
                                                                            overflowY: "auto",
                                                                            border: '1px solid #ccc',
                                                                            background: 'white',
                                                                            bordeRadius: '5px',
                                                                            padding: '5px',
                                                                        }}>
                                                                            <AccountsToTrade
                                                                                accounts={currentSettingsGroup.accountsToTrade}
                                                                            />
                                                                        </div>
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-3" controlId="tradingZones">
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Set Trading Zones",
                                                                                "For Krypton, you can set custom price ranges where Krypton will execute trades. If any trading zones are set, it will override Krypton's automated trading zones."
                                                                            )}
                                                                        </Form.Label>
                                                                        <Button
                                                                            className="mx-3"
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleOpenTradingZoneModal()}>
                                                                            Add Trading Zone
                                                                        </Button>
                                                                        <div style={{
                                                                            height: "200px",
                                                                            overflowY: "auto",
                                                                            border: '1px solid #ccc',
                                                                            background: 'white',
                                                                            bordeRadius: '5px',
                                                                            padding: '5px',
                                                                        }}>
                                                                            <TradingZonesTable
                                                                                tradingZones={currentSettingsGroup.tradingZones}
                                                                                handleEditZone={handleOpenTradingZoneEditModal}
                                                                                handleDeleteZone={handleDeleteTradingZone}
                                                                            />
                                                                        </div>
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col className="border-right">
                                                                    <Form.Group
                                                                        className="mb-1 pb-1"
                                                                        controlId="riskSize"
                                                                    >
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Average Risk Size ($)",
                                                                                "The dollar amount to risk for each trade. This is the average risk size ALM Trading will attempt to achieve over time. Some trades can risk more or less than this amount depending on the individual factors of each trade."
                                                                            )}
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Dollar Amount to Risk Each Trade on Average"
                                                                            value={currentSettingsGroup.riskSize}
                                                                            name="riskSize"
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </Form.Group>
                                                                    <Form.Group
                                                                        className="mb-1 pb-1"
                                                                        controlId="maxRiskSize"
                                                                    >
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Max Risk Size ($)",
                                                                                "The maximum dollar amount to risk on each trade. If you set this value and a trade exceeds this amount, the trade will not be executed on your account(s)."
                                                                            )}
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Maximum Dollar Amount to Risk Each Trade"
                                                                            value={currentSettingsGroup.maxRiskSize}
                                                                            name="maxRiskSize"
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-1 pb-1" controlId="profitTarget">
                                                                        <Form.Label>{InfoHover('Profit Target (Orion and Krypton Only)',
                                                                            'This setting applies to Orion and Krypton trades. Orion will set a profit target that is a multiple of the stop size for each trade. For example, if a stop size is 12 ticks trading ES and the profit target is 2, the profit target will be set at 24 ticks, or double the stop size. For Krypton, it will set the profit target at the larger value between this "Profit Target" setting and the Krypton optimized trades. For example, if Krypton sets a profit target at 2 times the stop size, and this setting is set to 2.5, your profit target will adjust to 2.5 times the stop size. If Krypton sets a profit target to 3 times the stop size, and you set this value to 2.5, your profit target for that trade will be 3 times the stop size.')}
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Profit Target"
                                                                            value={currentSettingsGroup.profitTarget}
                                                                            name="profitTarget"
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-1 pb-1" controlId="contractLimit">
                                                                        <Form.Label>{InfoHover('Contract Limit',
                                                                            'Specify the maximum number of contracts that will be open at any one time across all algorithms and all instruments. If the contract limit is reached, no new orders will be placed. If a new trade signal occurs that would exceed the contract limit, ALM Trading will use the remaining contracts left to reach the contract limit. For example, if you set a 10 contract limit, and you have 6 contracts open on ES, and a new order comes in for another 6 contracts, ALM Trading will place the new order at 4 contracts to fill in the remaining 10 contract limit.')}</Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Contract Limit"
                                                                            value={currentSettingsGroup.contractLimit}
                                                                            name="contractLimit"
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-1 pb-1" controlId="dailyLossLimit">
                                                                        <Form.Label>{InfoHover('Daily Loss Limit',
                                                                            'Specify the maximum daily loss limit. This value is monitored in real time, and if reached, orders on your account will be liquidated and no new orders will be placed for that day.')}</Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Daily Loss Limit"
                                                                            value={currentSettingsGroup.dailyLossLimit}
                                                                            name="dailyLossLimit"
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-3 pb-3" controlId="dailyProfitLimit">
                                                                        <Form.Label>{InfoHover('Daily Profit Limit',
                                                                            'Specify the maximum daily profit limit. This value is monitored in real time, and if reached, orders on your account will be liquidated and no new orders will be placed for that day.')}</Form.Label>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Daily Profit Limit"
                                                                            value={currentSettingsGroup.dailyProfitLimit}
                                                                            name="dailyProfitLimit"
                                                                            onChange={handleChange}
                                                                            required
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col>
                                                                    <div>
                                                                        <Button
                                                                            variant="outline-dark"
                                                                            onClick={(e) => ClearForm(e)}
                                                                        >
                                                                            Reset Form
                                                                        </Button>
                                                                    </div>
                                                                </Col>

                                                                <Col className="d-flex justify-content-center">
                                                                    <Button
                                                                        type="submit"
                                                                        onClick={async (e) => await SubmitTradeConfig(e)}
                                                                    >
                                                                        Submit
                                                                    </Button>
                                                                </Col>
                                                                <Col>
                                                                </Col>
                                                            </Row>
                                                        </Form >
                                                    )}
                                            </Col >
                                            <Col></Col>
                                        </Row >
                                    </Container >
                                    <Container>
                                        <Row>
                                            <Col></Col>
                                            <Col xs={10} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left">
                                                <Row className="border-bottom mb-2 pb-2">
                                                    <Col>
                                                        <h3>
                                                            Trade Log
                                                        </h3>
                                                    </Col>
                                                    <Col className="d-flex justify-content-end">
                                                        <ButtonGroup>
                                                            <Button
                                                                className="me-2"
                                                                variant="outline-dark"
                                                                onClick={() => getTradeLog('personal')}>
                                                                Refresh
                                                            </Button>
                                                        </ButtonGroup>
                                                        <ButtonGroup>
                                                            <Button
                                                                variant="danger"
                                                                onClick={() => liquidate()}>
                                                                Flatten All
                                                            </Button>
                                                        </ButtonGroup>
                                                    </Col>
                                                </Row>
                                                <TradeTable />
                                                {/* <Tabs
                                                    defaultActiveKey="your-trade-log"
                                                    transition={false}
                                                    className="mb-3"
                                                    fill
                                                >
                                                    <Tab eventKey="your-trade-log" title="My Trade Log">
                                                        <TradeTable />
                                                    </Tab>
                                                    <Tab eventKey="full-trade-log" title="Full Trade Log">
                                                        <FullTradeTable />
                                                    </Tab>
                                                </Tabs> */}
                                            </Col>
                                            <Col></Col>
                                        </Row>
                                    </Container>
                                </div>
                            )
                        }
                    </>
                )
            }
        </>
    )
}

